// Task Execution Service
// Manages task_executions collection for Phase 3 enhanced execution tracking

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { riskCalculationService } from './riskCalculationService';

/** Remove undefined values from an object (Firestore does not allow undefined). */
function omitUndefined(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(omitUndefined).filter(v => v !== undefined);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    out[k] = omitUndefined(v);
  }
  return out;
}

export class TaskExecutionService {
  constructor() {
    this.executionsCollection = collection(db, 'task_executions');
  }
  
  /**
   * Create new task execution record
   */
  async createExecution(taskId, protocolId, executorId, executorName, executorRole, userId) {
    try {
      const now = Timestamp.now();
      const task = await this.getTask(taskId);
      
      if (!task) throw new Error('Task not found');
      
      // Get risk data from task
      const riskData = task.risk_data || {
        protocol_max_rpn: task.baseRPN || 0,
        active_risks: [],
        risk_alerts_shown: 0,
        high_risk_actions: 0
      };
      
      // Create execution record
      const execution = {
        task_id: taskId,
        protocol_id: protocolId,
        
        // Executor
        executor_id: executorId,
        executor_name: executorName,
        executor_role: executorRole,
        
        // Timing
        scheduled_start: task.scheduledStart || now,
        actual_start: now,
        estimated_duration: task.estimatedDuration || 0,
        actual_duration: null,
        
        // Execution state
        status: 'in_progress',
        current_step: 0,
        current_action: 0,
        
        // Step tracking (initialized empty, filled during execution)
        steps_completed: [],
        
        // Risk monitoring
        risk_data: riskData,
        
        // Compliance (initialized, calculated during/after execution)
        compliance_score: null,
        compliance_breakdown: {
          timing_score: null,
          completeness_score: null,
          accuracy_score: null,
          evidence_score: null
        },
        
        // Deviations (initialized empty, filled during execution)
        deviations: [],
        
        // Completion
        completed: false,
        completed_at: null,
        completion_notes: null,
        verified_by: null,
        verification_status: 'pending',
        
        // Audit
        created_at: now,
        updated_at: now,
        execution_log: [{
          timestamp: now,
          event: 'execution_started',
          details: {
            task_id: taskId,
            protocol_id: protocolId,
            executor_id: executorId
          }
        }]
      };
      
      const docRef = await addDoc(this.executionsCollection, execution);
      
      console.log(`✅ Created task execution: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating task execution:', error);
      throw error;
    }
  }
  
  /**
   * Get execution by ID
   */
  async getExecution(executionId) {
    try {
      const docRef = doc(this.executionsCollection, executionId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } catch (error) {
      console.error('Error getting execution:', error);
      throw error;
    }
  }
  
  /**
   * Get execution by task ID
   */
  async getExecutionByTaskId(taskId) {
    try {
      const q = query(
        this.executionsCollection,
        where('task_id', '==', taskId),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const docSnap = querySnapshot.docs[0];
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } catch (error) {
      console.error('Error getting execution by task ID:', error);
      throw error;
    }
  }
  
  /**
   * Update execution step
   */
  async updateStep(executionId, stepData, userId) {
    try {
      const execution = await this.getExecution(executionId);
      if (!execution) throw new Error('Execution not found');
      
      const now = Timestamp.now();
      
      // Add or update step in steps_completed
      const stepsCompleted = [...(execution.steps_completed || [])];
      const stepIndex = stepsCompleted.findIndex(s => s.step_id === stepData.step_id);
      
      const toMs = (v) => {
        if (!v) return null;
        if (typeof v?.toMillis === 'function') return v.toMillis();
        if (typeof v?.getTime === 'function') return v.getTime();
        return null;
      };
      if (stepIndex >= 0) {
        // Update existing step (avoid undefined for Firestore)
        const endMs = toMs(stepData.completed_at);
        const startMs = toMs(stepData.started_at);
        const durationSec = stepData.duration_seconds ?? (endMs != null && startMs != null ? Math.floor((endMs - startMs) / 1000) : null);
        stepsCompleted[stepIndex] = omitUndefined({
          ...stepsCompleted[stepIndex],
          ...stepData,
          completed_at: now,
          duration_seconds: durationSec
        });
      } else {
        // Add new step (no undefined values)
        stepsCompleted.push(omitUndefined({
          step_id: stepData.step_id,
          step_number: stepData.step_number ?? stepsCompleted.length + 1,
          started_at: stepData.started_at || now,
          completed_at: now,
          duration_seconds: stepData.duration_seconds ?? 0,
          actions_completed: (stepData.actions_completed || []).map(a => omitUndefined(a)),
          validation_passed: stepData.validation_passed ?? true,
          deviation_detected: stepData.deviation_detected ?? false,
          deviation_details: stepData.deviation_details ?? null
        }));
      }
      
      // Update execution (strip undefined – Firestore does not allow undefined)
      const updatePayload = omitUndefined({
        steps_completed: stepsCompleted,
        current_step: stepData.step_number ?? stepsCompleted.length,
        updated_at: now,
        execution_log: [
          ...(execution.execution_log || []),
          {
            timestamp: now,
            event: 'step_completed',
            details: {
              step_id: stepData.step_id,
              step_number: stepData.step_number,
              duration_seconds: stepsCompleted[stepsCompleted.length - 1].duration_seconds
            }
          }
        ]
      });
      await updateDoc(doc(this.executionsCollection, executionId), updatePayload);
      
      console.log(`✅ Updated step ${stepData.step_number} in execution: ${executionId}`);
    } catch (error) {
      console.error('Error updating step:', error);
      throw error;
    }
  }
  
  /**
   * Add deviation to execution
   */
  async addDeviation(executionId, deviationData, userId) {
    try {
      const execution = await this.getExecution(executionId);
      if (!execution) throw new Error('Execution not found');
      
      const now = Timestamp.now();
      
      const deviation = {
        deviation_id: `dev_${Date.now()}`,
        deviation_type: deviationData.deviation_type,
        step_number: deviationData.step_number,
        action_number: deviationData.action_number || null,
        description: deviationData.description,
        severity: deviationData.severity || 'minor',
        corrective_action: deviationData.corrective_action || '',
        resolved: false,
        resolved_by: null,
        resolved_at: null
      };
      
      const deviations = [...(execution.deviations || []), deviation];
      
      await updateDoc(doc(this.executionsCollection, executionId), {
        deviations,
        updated_at: now,
        execution_log: [
          ...(execution.execution_log || []),
          {
            timestamp: now,
            event: 'deviation_detected',
            details: deviation
          }
        ]
      });
      
      console.log(`✅ Added deviation to execution: ${executionId}`);
      return deviation.deviation_id;
    } catch (error) {
      console.error('Error adding deviation:', error);
      throw error;
    }
  }
  
  /**
   * Update execution status (in_progress, paused, deviation_review, completed, failed)
   */
  async updateStatus(executionId, status, userId) {
    try {
      const execution = await this.getExecution(executionId);
      if (!execution) throw new Error('Execution not found');
      const now = Timestamp.now();
      const payload = omitUndefined({
        status,
        updated_at: now,
        ...(status === 'paused' && { paused_at: now }),
        ...(status === 'in_progress' && execution.status === 'paused' && { resumed_at: now }),
        execution_log: [
          ...(execution.execution_log || []),
          {
            timestamp: now,
            event: 'status_changed',
            details: { new_status: status, changed_by: userId }
          }
        ]
      });
      await updateDoc(doc(this.executionsCollection, executionId), payload);
      
      console.log(`✅ Updated execution status to ${status}: ${executionId}`);
    } catch (error) {
      console.error('Error updating execution status:', error);
      throw error;
    }
  }
  
  /**
   * Complete execution
   */
  async completeExecution(executionId, completionData, userId) {
    try {
      const execution = await this.getExecution(executionId);
      if (!execution) throw new Error('Execution not found');
      
      const now = Timestamp.now();
      
      // Calculate actual duration
      const actualDuration = execution.actual_start 
        ? Math.floor((now.toMillis() - execution.actual_start.toMillis()) / 1000)
        : 0;
      
      await updateDoc(doc(this.executionsCollection, executionId), {
        status: 'completed',
        completed: true,
        completed_at: now,
        actual_duration: actualDuration,
        completion_notes: completionData.completion_notes || null,
        verified_by: completionData.verified_by || null,
        verification_status: completionData.verification_status || 'pending',
        compliance_score: completionData.compliance_score || null,
        compliance_breakdown: completionData.compliance_breakdown || null,
        updated_at: now,
        execution_log: [
          ...(execution.execution_log || []),
          {
            timestamp: now,
            event: 'execution_completed',
            details: {
              actual_duration: actualDuration,
              compliance_score: completionData.compliance_score
            }
          }
        ]
      });
      
      console.log(`✅ Completed execution: ${executionId}`);
    } catch (error) {
      console.error('Error completing execution:', error);
      throw error;
    }
  }
  
  /**
   * Get task from taskService (helper method)
   */
  async getTask(taskId) {
    try {
      const { taskService } = await import('./taskService');
      return await taskService.getTask(taskId);
    } catch (error) {
      console.error('Error getting task:', error);
      return null;
    }
  }
}

export const taskExecutionService = new TaskExecutionService();

